import React, { useState, useCallback, useEffect } from "react"
import { ThemeProvider } from "styled-components"

import { useLocation } from "store/contexts/location"
import { useComponentizedPage } from "store/contexts/componentized-page"
import componentThemeExtension from "utils/componentThemeExtension"
import useEmailSubscription from "hooks/useEmailSubscription"
import useContactZendesk from "hooks/useContactZendesk"
import useClassFreePreview from "hooks/useClassFreePreview"
import useWebinarSignup from "hooks/useWebinarSignup"
import useTracking from "hooks/useTracking"
import useFormTracking from "hooks/useFormTracking"

import SignUpForm from "components/sign-up"
import PopUpForm from "components/popup-form"
import Contact from "components/button/contact"
import RequestMeeting from "components/request-meeting"

const ContentfulSignupForm = ({
  className,
  component,
  nested,
  elRef,
  popupForm = false,
  isPromotionalPlacement,
  multiListSignupForms = [],
  isMultiListSignupForm,
}) => {
  const { class: pageClass } = useComponentizedPage()
  const {
    heading,
    headingFont,
    headingLogo,
    backgroundColor,
    bodyText,
    optionalEmailFields,
    requiredEmailFields,
    signupType,
    backgroundImage,
    emailList,
    emailSource,
    parameterForList,
    parameterForSource,
    targetClassOverride,
    tracking,
    trackingId,
    webinarId,
    successMessageOverride,
    ctaText,
    ctaColor,
    navigationAnchor,
    id,
    hideOnPageLoad,
    postSignupRedirectUrl,
    webinarPlatform,
    onEmailSubscribe,
    meetingScheduleUrl,
    theme,
  } = component

  const [popUp, setPopUp] = useState(false)

  const { location } = useLocation()
  // case-insensitive query parameters
  const queryParams = new URLSearchParams(
    [...new URLSearchParams(location.search)].map(([key, value]) => [key.toLowerCase(), value]),
  )
  // override emailList/emailSource if query params match parameterForList/parameterForSource
  const list =
    parameterForList && queryParams.has(parameterForList.toLowerCase())
      ? queryParams.get(parameterForList.toLowerCase())
      : emailList
  const source =
    parameterForSource && queryParams.has(parameterForSource.toLowerCase())
      ? queryParams.get(parameterForSource.toLowerCase())
      : emailSource

  const { trackRender, trackView, track } = useTracking()
  const { trackEmailFormView, trackWebinarFormView } = useFormTracking()

  if (signupType === "Email List") {
    trackEmailFormView(elRef, trackingId, { signup_source: source, list })
  }

  if (signupType === "Webinar") {
    trackWebinarFormView(elRef, trackingId, { webinar_id: webinarId })
  }

  if (tracking && tracking.includes("Render")) {
    trackRender(elRef, trackingId)
  }

  if (tracking && tracking.includes("View")) {
    trackView(elRef, trackingId)
  }

  const [subscribing, subscribed, errEmail, subscribeEmail, subscribedEmail] =
    useEmailSubscription(trackingId)

  const {
    contacting,
    contacted,
    zendeskError: errZendesk,
    contactZendesk,
    onClosePopup,
  } = useContactZendesk(trackingId)
  const [posting, errClass, createFreeTrial] = useClassFreePreview()
  const [signingUp, signedUp, errWebinar, signupWebinar] = useWebinarSignup(webinarId, trackingId)

  const cls = targetClassOverride || pageClass

  useEffect(() => {
    if (subscribed && onEmailSubscribe && subscribedEmail) {
      onEmailSubscribe(subscribedEmail)
    }
  }, [subscribed, onEmailSubscribe, subscribedEmail])

  const submitHandler = useCallback(
    (data) => {
      if (signupType === "Email List") {
        const { email, ...props } = data
        subscribeEmail(email, list, source, props, postSignupRedirectUrl, multiListSignupForms)
      }

      if (signupType === "Class Trial") {
        const { email } = data
        createFreeTrial(cls.classId, email)
      }

      if (signupType === "Webinar") {
        signupWebinar(
          data.email,
          data.firstName || data.fullName || "anonymous",
          data.scheduleId,
          postSignupRedirectUrl,
          webinarPlatform,
          data.webinarQuestion,
        )
        track("Webinar Registered", {
          webinarId: trackingId,
        })
      }

      if (signupType === "Contact Form") {
        contactZendesk(data)
      }
    },
    [
      signupType,
      subscribeEmail,
      list,
      source,
      postSignupRedirectUrl,
      createFreeTrial,
      cls,
      signupWebinar,
      webinarPlatform,
      multiListSignupForms,
      contactZendesk,
    ],
  )

  const onTogglePopup = () => {
    setPopUp(!popUp)
    onClosePopup()
  }
  const headingText = heading && heading.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")

  let submittedText =
    signupType === "Email List"
      ? "<p>You have been subscribed!</p>"
      : signupType === "Contact Form"
      ? "<p>Your message has been sent!<p>"
      : "<p>You have been registered!</p>"

  if (successMessageOverride) {
    submittedText = successMessageOverride.childMarkdownRemark.html
  }

  if (signupType === "Webinar" && signedUp && webinarPlatform !== "Crowdcast") {
    submittedText += `<p>Your webinar link: <a href="${signedUp}" target="_blank">${signedUp}</a></p>`
  }

  if (signupType === "Meeting Request") {
    return <RequestMeeting segmentTrackingId={trackingId} meetingScheduleUrl={meetingScheduleUrl} />
  }

  if (popupForm) {
    return (
      <ThemeProvider theme={componentThemeExtension(backgroundColor, nested, false, theme)}>
        {popUp && (
          <PopUpForm
            className={className}
            hideOnPageLoad={hideOnPageLoad}
            dataId={id}
            id={navigationAnchor && navigationAnchor.anchorId}
            elRef={elRef}
            title={headingText}
            ctaText={ctaText || "Send"}
            requiredFields={["Email"].concat(requiredEmailFields || [])}
            optionalFields={optionalEmailFields || []}
            submitting={contacting}
            hasError={errZendesk}
            submitPrimary={ctaColor === "Primary CTA"}
            postSignupRedirectUrl={postSignupRedirectUrl}
            onClose={onTogglePopup}
            trackingId={trackingId}
            successMessageOverride={successMessageOverride}
            submitted={contacted && submittedText}
            onSubmit={submitHandler}
          />
        )}
        {!popUp && <Contact onClick={onTogglePopup} />}
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={componentThemeExtension(backgroundColor, nested, false, theme)}>
      <SignUpForm
        className={className}
        hideOnPageLoad={hideOnPageLoad}
        dataId={id}
        id={navigationAnchor && navigationAnchor.anchorId}
        elRef={elRef}
        title={headingText}
        subtitle={bodyText && bodyText.childMarkdownRemark.html}
        useMonoline={headingFont === "mbg Monoline"}
        webinarId={webinarId}
        ctaText={
          ctaText ||
          (signupType === "Email List"
            ? "Sign Up"
            : signupType === "Class Trial"
            ? "Start My Free Preview"
            : signupType === "Contact Form"
            ? "Send"
            : "Claim My Free Spot")
        }
        backgroundImage={backgroundImage && backgroundImage[0].secure_url}
        requiredFields={["Email"].concat(requiredEmailFields || [])}
        optionalFields={optionalEmailFields}
        onSubmit={submitHandler}
        submitting={subscribing || posting || signingUp || contacting}
        hasError={errEmail || errClass || errWebinar || errZendesk}
        submitted={(subscribed || signedUp || contacted) && submittedText}
        submitPrimary={ctaColor === "Primary CTA"}
        postSignupRedirectUrl={postSignupRedirectUrl}
        signupType={signupType}
        isPromotionalPlacement={isPromotionalPlacement}
        isMultiListSignupForm={isMultiListSignupForm}
      />
    </ThemeProvider>
  )
}

export default ContentfulSignupForm
